import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  CreditCard,
  Smartphone,
  Landmark,
  Wallet,
  ShieldCheck,
  BarChart3,
  Zap,
  Bell,
  Package,
  FlaskConical,
  Plus,
  Minus,
  Star,
  ArrowRight,
  ArrowUpRight,
} from "lucide-react";
import gatewayImg from "../Accets/fin.jpeg";
import {
  RevealSection,
  SplitText,
  MagneticButton,
  staggerContainer,
  staggerItem,
  EASE,
} from "../components/motion/ScrollFX";

// ================= THEME =================
const theme = {
  ink: "#221619",
  inkMuted: "#6B6265",
  accent: "#E63946",
  accentDeep: "#B22234",
  gold: "#D9A441",
  border: "#E6DFDC",
  surface: "#FBF8F6",
  surfaceAlt: "#F2ECE9",
  heroBg: "#1A0E10",
  darkBand: "#221619",
  darkBandDeep: "#160B0D",
  ctaBg: "#2B1013",
  ctaBgDeep: "#160A0C",
};

// ================= DATA =================
const features = [
  { icon: CreditCard, title: "Card Payments", desc: "Accept Visa, Mastercard, RuPay and American Express securely." },
  { icon: Smartphone, title: "UPI Payments", desc: "Accept payments through all major UPI applications instantly." },
  { icon: Landmark, title: "Net Banking", desc: "Support payments from all leading Indian banks." },
  { icon: Wallet, title: "Wallets", desc: "Integrate popular digital wallets for quick checkouts." },
  { icon: ShieldCheck, title: "Secure Transactions", desc: "PCI DSS compliant with advanced fraud protection." },
  { icon: BarChart3, title: "Analytics Dashboard", desc: "Monitor transactions, settlements and reports in real time." },
];

const steps = [
  { n: "1", title: "Integrate API", desc: "Connect our payment gateway with your website or mobile app using simple APIs." },
  { n: "2", title: "Customer Pays", desc: "Customers choose UPI, Cards, Net Banking or Wallets to complete payment." },
  { n: "3", title: "Secure Processing", desc: "Every transaction is encrypted and processed securely with advanced fraud protection." },
  { n: "4", title: "Instant Settlement", desc: "Funds are transferred quickly to your business account with real-time tracking." },
];

const trustStats = [
  { n: "99.99%", t: "Uptime", d: "Reliable infrastructure for uninterrupted payments." },
  { n: "500+", t: "Businesses", d: "Trusted by startups, SMEs and enterprises." },
  { n: "10M+", t: "Transactions", d: "Securely processed every year." },
  { n: "24×7", t: "Support", d: "Dedicated experts available whenever you need help." },
];

const devHighlights = [
  { icon: Zap, label: "REST APIs" },
  { icon: Bell, label: "Webhook Support" },
  { icon: Package, label: "SDK Libraries" },
  { icon: FlaskConical, label: "Sandbox Environment" },
];

const faqs = [
  { question: "How quickly can I integrate the Payment Gateway?", answer: "Our APIs are developer-friendly and can be integrated in just a few hours using our SDKs and documentation." },
  { question: "Which payment methods are supported?", answer: "We support UPI, Credit Cards, Debit Cards, Net Banking, Wallets and EMI options." },
  { question: "Is the payment gateway secure?", answer: "Yes. We use bank-grade encryption, PCI-DSS compliance, fraud detection and secure authentication." },
  { question: "How long does settlement take?", answer: "Most settlements are processed within T+1 working day depending on your account configuration." },
  { question: "Do you provide developer support?", answer: "Absolutely! Our technical support team is available to help with API integration, testing and deployment." },
];

const testimonials = [
  { text: "Integration was incredibly simple. We started accepting payments within a single day and our success rate improved significantly.", name: "Rahul Sharma", role: "Founder, ShopEase" },
  { text: "Excellent developer documentation and outstanding support team. Their APIs are clean and easy to integrate.", name: "Priya Verma", role: "CTO, FinTech Solutions" },
  { text: "Fast settlements, secure payments and a seamless customer experience. Exactly what our business needed.", name: "Aman Gupta", role: "CEO, TravelHub" },
];

function Eyebrow({ children, center = false, light = false }) {
  return (
    <div
      className={`inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase mb-4 ${center ? "mx-auto" : ""}`}
      style={{ color: light ? "#FF8A93" : theme.accent }}
    >
      <span className="h-px w-6" style={{ backgroundColor: light ? "#FF8A93" : theme.accent }} />
      {children}
    </div>
  );
}

function DotGrid({ className = "", light = false }) {
  return (
    <div
      className={`pointer-events-none absolute inset-0 ${light ? "opacity-[0.05]" : "opacity-[0.06]"} ${className}`}
      style={{
        backgroundImage: `radial-gradient(${light ? "#FFFFFF" : theme.ink} 1px, transparent 1px)`,
        backgroundSize: "26px 26px",
      }}
    />
  );
}

export default function PaymentGateway() {
  const [openFAQ, setOpenFAQ] = useState(null);

  return (
    <div className="w-full" style={{ backgroundColor: theme.surface }}>
      <style>{`
        .pg-glow-a {
          position: absolute;
          width: 40rem;
          height: 40rem;
          top: 50%;
          left: 50%;
          margin: -20rem 0 0 -20rem;
          border-radius: 9999px;
          filter: blur(90px);
          opacity: 0.32;
          background: radial-gradient(circle, ${theme.accent}, transparent 65%);
          animation: pgGlowA 24s ease-in-out infinite;
        }
        @keyframes pgGlowA {
          0%   { transform: translate(-9rem, -4rem) scale(1); }
          35%  { transform: translate(8rem, -2rem) scale(1.1); }
          70%  { transform: translate(3rem, 8rem) scale(0.9); }
          100% { transform: translate(-9rem, -4rem) scale(1); }
        }
        .pg-glow-b {
          position: absolute;
          width: 26rem;
          height: 26rem;
          top: 50%;
          left: 50%;
          margin: -13rem 0 0 -13rem;
          border-radius: 9999px;
          filter: blur(95px);
          opacity: 0.24;
          background: radial-gradient(circle, ${theme.gold}, transparent 65%);
          animation: pgGlowB 30s ease-in-out infinite;
        }
        @keyframes pgGlowB {
          0%   { transform: translate(7rem, 4rem) scale(1); }
          40%  { transform: translate(-7rem, -2rem) scale(0.9); }
          75%  { transform: translate(-1rem, -8rem) scale(1.15); }
          100% { transform: translate(7rem, 4rem) scale(1); }
        }
        .card-glow {
          pointer-events: none;
          position: absolute;
          top: -3rem;
          right: -3rem;
          width: 9rem;
          height: 9rem;
          border-radius: 9999px;
          filter: blur(30px);
          background: ${theme.accent};
          opacity: 0.08;
          transition: opacity 0.3s ease;
        }
        .group:hover .card-glow { opacity: 0.16; }
        @media (prefers-reduced-motion: reduce) {
          .pg-glow-a, .pg-glow-b { animation: none !important; }
        }
      `}</style>

      {/* ================= HERO (dark) ================= */}
      <section className="relative overflow-hidden pt-40 pb-24 md:pt-48" style={{ backgroundColor: theme.heroBg }}>
        <span className="pg-glow-a" />
        <span className="pg-glow-b" />
        <DotGrid light />

        <div className="relative z-10 max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-14 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            <Eyebrow light>Payment Solutions</Eyebrow>
            <h1 className="text-4xl md:text-6xl font-extrabold leading-[1.08] tracking-tight text-white">
              <SplitText text="Smart Payment" className="text-white" />
              <br />
              <span style={{ color: theme.accent }}>Gateway</span>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5, ease: EASE }}
              className="mt-6 text-lg text-white/60 max-w-lg"
            >
              Accept UPI, Credit Cards, Debit Cards, Net Banking, Wallets and
              International Payments through a single API.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.65, ease: EASE }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <MagneticButton>
                <Link
                  to="/contact"
                  data-cursor="hover"
                  style={{ backgroundColor: theme.accent }}
                  className="group inline-flex items-center gap-2 text-white px-7 py-3.5 rounded-md font-semibold hover:brightness-110 transition-all"
                >
                  Get Started
                  <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </MagneticButton>
              <MagneticButton strength={0.25}>
                <Link
                  to="/contact"
                  data-cursor="hover"
                  className="inline-flex items-center gap-2 border border-white/25 text-white px-7 py-3.5 rounded-md font-medium hover:bg-white hover:text-[#221619] transition-colors"
                >
                  Contact Sales
                </Link>
              </MagneticButton>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
            className="relative"
          >
            <div
              className="absolute -inset-6 rounded-[2.5rem] blur-2xl opacity-40"
              style={{ background: `radial-gradient(circle, ${theme.accent}, transparent 70%)` }}
            />
            <img
              src={gatewayImg}
              alt="Payment Gateway"
              className="relative rounded-3xl shadow-2xl border border-white/10 w-full"
            />
          </motion.div>
        </div>
      </section>

      {/* ================= FEATURES (light, bento) ================= */}
      <section className="relative py-24 overflow-hidden" style={{ backgroundColor: theme.surfaceAlt }}>
        <DotGrid />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <RevealSection className="text-center max-w-3xl mx-auto mb-16">
            <Eyebrow center>What's Included</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-extrabold" style={{ color: theme.ink }}>
              Powerful <span style={{ color: theme.accent }}>Features</span>
            </h2>
            <p className="mt-4 text-lg" style={{ color: theme.inkMuted }}>
              Everything you need to collect payments online.
            </p>
          </RevealSection>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {features.map((item) => (
              <motion.div
                variants={staggerItem}
                key={item.title}
                data-cursor="hover"
                whileHover={{ y: -6, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 260, damping: 18 }}
                className="group relative overflow-hidden rounded-3xl p-8 border shadow-sm hover:shadow-2xl"
                style={{ backgroundColor: "#FFFFFF", borderColor: theme.border }}
              >
                <span className="card-glow" />
                <div
                  className="relative w-14 h-14 flex items-center justify-center rounded-xl mb-6 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6"
                  style={{ backgroundColor: theme.surfaceAlt, color: theme.accent }}
                >
                  <item.icon size={24} />
                </div>
                <h3 className="relative text-xl font-semibold" style={{ color: theme.ink }}>{item.title}</h3>
                <p className="relative mt-3 leading-relaxed" style={{ color: theme.inkMuted }}>{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= HOW IT WORKS (light) ================= */}
      <section className="py-24" style={{ backgroundColor: theme.surface }}>
        <div className="max-w-7xl mx-auto px-6">
          <RevealSection className="text-center max-w-3xl mx-auto mb-16">
            <Eyebrow center>The Flow</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-extrabold" style={{ color: theme.ink }}>How It Works</h2>
            <p className="mt-4 text-lg" style={{ color: theme.inkMuted }}>
              Start accepting payments in just four simple steps.
            </p>
          </RevealSection>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid md:grid-cols-4 gap-6"
          >
            {steps.map((item) => (
              <motion.div
                key={item.n}
                variants={staggerItem}
                whileHover={{ y: -6 }}
                data-cursor="hover"
                className="text-center rounded-3xl p-8 border shadow-sm"
                style={{ backgroundColor: "#FFFFFF", borderColor: theme.border }}
              >
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center mx-auto text-xl font-bold text-white"
                  style={{ backgroundColor: theme.accent }}
                >
                  {item.n}
                </div>
                <h3 className="text-xl font-semibold mt-6" style={{ color: theme.ink }}>{item.title}</h3>
                <p className="mt-3" style={{ color: theme.inkMuted }}>{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= WHY CHOOSE US (dark) ================= */}
      <section className="relative py-24 overflow-hidden" style={{ backgroundColor: theme.darkBand }}>
        <DotGrid light />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <RevealSection className="text-center max-w-3xl mx-auto mb-16">
            <Eyebrow light center>Why Choose Us</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-extrabold text-white">
              Trusted by Growing <span style={{ color: theme.accent }}>Businesses</span>
            </h2>
            <p className="mt-4 text-lg text-white/60">
              Trusted by businesses of all sizes for secure, fast and reliable online payments.
            </p>
          </RevealSection>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid md:grid-cols-4 gap-6"
          >
            {trustStats.map((s) => (
              <motion.div
                key={s.t}
                variants={staggerItem}
                whileHover={{ y: -6 }}
                data-cursor="hover"
                className="text-center rounded-3xl p-8 border"
                style={{ backgroundColor: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.1)" }}
              >
                <h3 className="text-4xl font-extrabold" style={{ color: theme.accent }}>{s.n}</h3>
                <p className="mt-3 font-semibold text-white">{s.t}</p>
                <p className="mt-2 text-sm text-white/50">{s.d}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= DEVELOPER API (light) ================= */}
      <section className="py-24" style={{ backgroundColor: theme.surfaceAlt }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <RevealSection>
              <Eyebrow>Developer Friendly</Eyebrow>
              <h2 className="text-4xl md:text-5xl font-extrabold leading-tight" style={{ color: theme.ink }}>
                Integrate in Minutes with Our APIs
              </h2>
              <p className="mt-6 text-lg leading-relaxed" style={{ color: theme.inkMuted }}>
                Powerful REST APIs, SDKs and webhooks make integrating our
                payment gateway effortless. Whether you're building an
                e-commerce platform, SaaS application or mobile app, our APIs
                are designed for speed, security and scalability.
              </p>

              <div className="grid grid-cols-2 gap-6 mt-10">
                {devHighlights.map((item) => (
                  <div key={item.label} className="flex items-center gap-3">
                    <span
                      className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                      style={{ backgroundColor: `${theme.accent}1A`, color: theme.accent }}
                    >
                      <item.icon size={16} />
                    </span>
                    <span style={{ color: theme.ink }} className="font-medium">{item.label}</span>
                  </div>
                ))}
              </div>

              <MagneticButton>
                <Link
                  to="/contact"
                  data-cursor="hover"
                  style={{ backgroundColor: theme.accent }}
                  className="mt-10 inline-flex items-center gap-2 px-7 py-3.5 rounded-md text-white font-semibold hover:brightness-110 transition-all"
                >
                  View API Documentation
                  <ArrowRight size={16} />
                </Link>
              </MagneticButton>
            </RevealSection>

            <RevealSection delay={0.1}>
              <div
                className="rounded-3xl border overflow-hidden shadow-xl"
                style={{ backgroundColor: theme.darkBand, borderColor: "rgba(255,255,255,0.1)" }}
              >
                <div className="px-6 py-4 border-b flex gap-2" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
                  <div className="w-3 h-3 bg-white/20 rounded-full" />
                  <div className="w-3 h-3 bg-white/20 rounded-full" />
                  <div className="w-3 h-3 bg-white/20 rounded-full" />
                </div>
                <pre className="p-8 text-sm overflow-x-auto font-mono leading-relaxed" style={{ color: "#8FD3C4" }}>
{`POST /v1/payment/create

{
  "amount": 1000,
  "currency": "INR",
  "customer": {
      "name": "John Doe",
      "email": "john@example.com"
  },
  "payment_method": "UPI"
}

Response:

{
  "status": "success",
  "payment_id": "pay_123456",
  "redirect_url": "https://..."
}`}
                </pre>
              </div>
            </RevealSection>
          </div>
        </div>
      </section>

      {/* ================= FAQ (light) ================= */}
      <section className="py-24" style={{ backgroundColor: theme.surface }}>
        <div className="max-w-4xl mx-auto px-6">
          <RevealSection className="text-center mb-14">
            <Eyebrow center>Frequently Asked Questions</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-extrabold" style={{ color: theme.ink }}>Got Questions?</h2>
            <p className="mt-4 text-lg" style={{ color: theme.inkMuted }}>
              Everything you need to know about our Payment Gateway.
            </p>
          </RevealSection>

          <div className="space-y-4">
            {faqs.map((faq, index) => {
              const isOpen = openFAQ === index;
              return (
                <motion.div
                  key={faq.question}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.06 }}
                  className="rounded-2xl border overflow-hidden"
                  style={{ backgroundColor: "#FFFFFF", borderColor: theme.border }}
                >
                  <button
                    onClick={() => setOpenFAQ(isOpen ? null : index)}
                    data-cursor="hover"
                    className="w-full flex justify-between items-center gap-4 p-6 text-left"
                  >
                    <h3 className="text-lg font-semibold" style={{ color: theme.ink }}>{faq.question}</h3>
                    <span
                      className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors"
                      style={{ backgroundColor: `${theme.accent}1A`, color: theme.accent }}
                    >
                      {isOpen ? <Minus size={16} /> : <Plus size={16} />}
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                        className="overflow-hidden"
                      >
                        <p className="px-6 pb-6 leading-relaxed" style={{ color: theme.inkMuted }}>{faq.answer}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= TESTIMONIALS (dark) ================= */}
      <section className="relative py-24 overflow-hidden" style={{ backgroundColor: theme.darkBandDeep }}>
        <DotGrid light />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <RevealSection className="text-center max-w-3xl mx-auto mb-16">
            <Eyebrow light center>Customer Success</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-extrabold text-white">
              Loved by <span style={{ color: theme.accent }}>Businesses</span>
            </h2>
            <p className="mt-4 text-lg text-white/60">
              Businesses across industries rely on our Payment Gateway for
              secure, fast and reliable transactions.
            </p>
          </RevealSection>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-6"
          >
            {testimonials.map((t) => (
              <motion.div
                key={t.name}
                variants={staggerItem}
                whileHover={{ y: -6 }}
                data-cursor="hover"
                className="rounded-3xl p-8 border"
                style={{ backgroundColor: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.1)" }}
              >
                <div className="flex gap-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} size={16} fill={theme.gold} color={theme.gold} />
                  ))}
                </div>
                <p className="mt-6 leading-relaxed text-white/70">"{t.text}"</p>
                <div className="mt-8">
                  <h4 className="font-semibold text-white">{t.name}</h4>
                  <p className="text-sm text-white/40">{t.role}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= CTA (dark) ================= */}
      <section
        className="relative py-28 overflow-hidden"
        style={{ background: `linear-gradient(160deg, ${theme.ctaBg} 0%, ${theme.ctaBgDeep} 100%)` }}
      >
        <DotGrid light />
        <div
          className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] rounded-full blur-3xl opacity-[0.14]"
          style={{ backgroundColor: theme.accent }}
        />
        <span
          aria-hidden="true"
          className="pointer-events-none select-none absolute -right-8 -top-8 opacity-[0.05]"
          style={{ color: "#FFFFFF" }}
        >
          <ArrowUpRight size={220} strokeWidth={1} />
        </span>

        <RevealSection className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <Eyebrow center light>Ready When You Are</Eyebrow>
          <h2 className="text-4xl md:text-5xl font-extrabold leading-tight text-white">
            Start accepting <span style={{ color: theme.accent }}>payments today</span>
          </h2>
          <p className="mt-6 text-lg text-white/60 max-w-xl mx-auto">
            Integrate our payment gateway and start collecting payments in
            minutes.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <MagneticButton>
              <Link
                to="/contact"
                data-cursor="hover"
                style={{ backgroundColor: theme.accent }}
                className="group inline-flex items-center gap-2 px-9 py-4 rounded-md text-white font-semibold text-lg shadow-lg shadow-black/30 hover:brightness-110 transition-all"
              >
                Get Started
                <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </MagneticButton>
          </div>
        </RevealSection>
      </section>
    </div>
  );
}